/**
 * 소셜 로그인 버튼 — **기본 로그인 수단** (사용자 결정 2026-09-11).
 *
 * 오픈데이터모델러와 같은 방식이다. 아이디/비밀번호는 남겨 두되 접어 두고, 여기가 먼저 보인다.
 *
 * ## 알아 둘 것 — 같은 이메일이라도 **다른 계정**이다
 *
 * QBase 는 `authProvider`(ID · GOOGLE · NAVER)를 계정 식별자에 넣는다. 그래서 예전에
 * `hong@x.com` 으로 가입한 사람이 Google 로 들어오면 **새 계정**이고, 전에 만든 사례·설정과
 * 이어지지 않는다. 이 사실을 화면에 적어 둔다 — 나중에 "데이터가 사라졌다" 로 온다.
 *
 * 제공자 SDK 는 `services/oauthClients.js` 한 곳에서만 만진다.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiLoader } from 'react-icons/fi';
import {
    hasGoogle, hasNaver, requestGoogleAccessToken, startNaverLogin,
} from '../services/oauthClients';

const GoogleMark = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
    </svg>
);

const NaverMark = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#ffffff" d="M16.273 12.845L7.376 0H0v24h7.726V11.156L16.624 24H24V0h-7.727v12.845z" />
    </svg>
);

// 기본값은 **인자에서** 준다. `defaultProps` 는 함수 컴포넌트에서 곧 사라지고,
// 그때까지 렌더할 때마다 콘솔에 경고가 쌓인다(실측 — 브라우저 콘솔에서 확인했다).
function SocialLoginButtons({ onGoogleToken, onNaverStart = null, busy = false, error = '' }) {
    const { t } = useTranslation();
    const [pending, setPending] = useState('');

    const google = hasGoogle();
    const naver = hasNaver();
    // 설정이 하나도 없으면 **버튼 대신 이유**를 보여 준다. 눌러도 아무 일이 없는 버튼을
    // 세워 두면 사용자는 로그인이 고장 난 줄 안다.
    if (!google && !naver) {
        return <div className="social-unconfigured">{t('auth:social.unconfigured')}</div>;
    }

    const clickGoogle = async () => {
        setPending('google');
        try {
            onGoogleToken(await requestGoogleAccessToken());
        } catch (err) {
            // 팝업을 닫은 것은 취소지 실패가 아니다 — 붉은 문구를 띄우지 않는다
            if (!err.cancelled) onGoogleToken(null, err);
        } finally {
            setPending('');
        }
    };

    const clickNaver = () => {
        setPending('naver');
        // 이 창이 네이버로 떠난다. 돌아오면 LoginPage 가 code 를 집어 이어서 처리한다.
        if (onNaverStart) onNaverStart();
        startNaverLogin();
    };

    const disabled = busy || !!pending;

    return (
        <div className="social-login">
            {google && (
                <button
                    type="button" className="social-btn social-google"
                    onClick={clickGoogle} disabled={disabled}
                >
                    {pending === 'google' ? <FiLoader className="social-spin" size={16} /> : <GoogleMark />}
                    <span>{t('auth:social.google')}</span>
                </button>
            )}
            {naver && (
                <button
                    type="button" className="social-btn social-naver"
                    onClick={clickNaver} disabled={disabled}
                >
                    {pending === 'naver' ? <FiLoader className="social-spin" size={16} /> : <NaverMark />}
                    <span>{t('auth:social.naver')}</span>
                </button>
            )}
            {/* 같은 이메일이라도 가입 경로가 다르면 **다른 계정**이다 — 먼저 말해 둔다 */}
            <div className="social-note">{t('auth:social.separateAccount')}</div>
            {error && <div className="social-error">{error}</div>}
        </div>
    );
}

SocialLoginButtons.propTypes = {
    /** (accessToken, err) — 토큰을 받았거나(err 없음) 실패했을 때(token null) */
    onGoogleToken: PropTypes.func.isRequired,
    /** 이 창이 네이버로 떠나기 직전 (떠나기 전에 저장할 것이 있으면) */
    onNaverStart: PropTypes.func,
    busy: PropTypes.bool,
    error: PropTypes.string,
};

export default SocialLoginButtons;
